/**
 * The scanner-safe landing for GoTrue signup, invite and email-change mail
 * (#2689) — the recovery screen's Continue step, for the three other flows.
 *
 * The mail carries `#type=<flow>&token_hash=…`. This screen reads and strips it
 * in its first render (`readAuthMailChallengeFromHash`), shows one explicit
 * Continue, and only that press calls GoTrue `POST /verify`. A mail scanner that
 * fetches the link renders this page and spends nothing.
 *
 *   signup        → the canonical #2668 adoption (`exchangeSignInChallenge`):
 *                   a confirmed signup signs the person in, as the old redirect did.
 *   invite        → a memory-only grant, then the recovery password ceremony
 *                   (`SetNewPassword`, purpose "invitation"); the person then
 *                   signs in with the password they chose.
 *   email_change  → confirmed, never a sign-in; the minted session is revoked.
 *
 * It is drawn from the same pieces as `ResetPassword` (the shared frame, card,
 * kit `Button`) — no stylesheet of its own.
 */
import { useRef, useState, type ReactNode } from 'react'
import { Button, useOptionalLocalization } from '@solum/ui'
import { useInjectedAuth } from './provider.js'
import { type SignInLink } from './SignIn.js'
import { AuthScreenFrame, CardLockup, SetNewPassword } from './ResetPassword.js'
import { readAuthMailChallengeFromHash, type AuthMailChallenge, type AuthMailFlow } from './mail-challenge.js'
import type { RecoveryGrant } from './recovery.js'
import s from './SignIn.module.css'

export interface AuthMailLandingProps {
  /** Leave the landing: into the app when a session now exists, else to sign-in. */
  onDone: () => void
  // ── the identity slots, identical to SignIn's and ResetPassword's ──
  logo?: string | null
  headline?: string | null
  tagline?: string | null
  assurance?: string | null
  legal?: SignInLink[] | null
  footnote?: string | null
  footer?: ReactNode
}

type LocalizedCopy = {
  title: readonly [key: string, fallback: string]
  sub: readonly [key: string, fallback: string]
  action: readonly [key: string, fallback: string]
  spent: readonly [key: string, fallback: string]
}

const COPY: Record<AuthMailFlow, LocalizedCopy> = {
  signup: {
    title: ['auth.mail.signup.title', 'Confirm your email address'],
    sub: ['auth.mail.signup.detail', 'Continue to confirm this address and finish setting up your account.'],
    action: ['auth.mail.signup.action', 'Confirm email address'],
    spent: ['auth.mail.signup.error.spent', 'That confirmation link has expired or has already been used. If you already confirmed this address, sign in. Otherwise sign up again to get a new link.'],
  },
  invite: {
    title: ['auth.mail.invite.title', 'Create your password'],
    sub: ['auth.mail.invite.detail', 'Continue to verify this link and choose your password. After that, sign in to accept your invitation.'],
    action: ['auth.mail.invite.action', 'Continue'],
    spent: ['auth.mail.invite.error.spent', 'That invitation link has expired or has already been used. Ask the person who invited you for a new invitation.'],
  },
  email_change: {
    title: ['auth.mail.email_change.title', 'Confirm your new email address'],
    sub: ['auth.mail.email_change.detail', 'Continue to confirm the change to your sign-in address.'],
    action: ['auth.mail.email_change.action', 'Confirm email change'],
    spent: ['auth.mail.email_change.error.spent', 'That confirmation link has expired or has already been used. Request the email change again.'],
  },
}

type Phase = 'continue' | 'password' | 'confirmed' | 'awaiting_other_address' | 'refused'

export function AuthMailLanding(props: AuthMailLandingProps) {
  const { onDone, logo, headline, tagline, assurance, legal, footnote, footer } = props
  const localization = useOptionalLocalization()
  const { client } = useInjectedAuth()
  const message = (key: string, fallback: string) => localization?.message(key, fallback) ?? fallback
  // Read ONCE, before paint (useState's initialiser), exactly as ResetPassword.
  const [initial] = useState(() => readAuthMailChallengeFromHash())
  const challenge = useRef<AuthMailChallenge | null>(initial.challenge)
  const grant = useRef<RecoveryGrant | null>(null)
  const flow = initial.challenge?.flow ?? null
  const [phase, setPhase] = useState<Phase>(initial.challenge ? 'continue' : 'refused')
  const [refusal, setRefusal] = useState<string>(initial.error?.description
    ?? message('auth.mail.error.invalid', 'That link is not valid. Request a new one.'))
  const [busy, setBusy] = useState(false)

  async function proceed() {
    const current = challenge.current
    if (!current || busy) return
    // One press spends the challenge; a double-click cannot replay it.
    challenge.current = null
    setBusy(true)
    try {
      if (current.flow === 'signup') {
        await client.exchangeSignInChallenge({ tokenHash: current.tokenHash, verificationType: 'signup' })
        onDone()
      } else if (current.flow === 'invite') {
        grant.current = await client.exchangeInviteTokenHash(current.tokenHash)
        setPhase('password')
      } else {
        setPhase(await client.confirmEmailChangeTokenHash(current.tokenHash))
      }
    } catch {
      // GoTrue answers expired and used alike (`otp_expired`); the screen says
      // both, and never echoes provider text.
      setRefusal(message(...COPY[current.flow].spent))
      setPhase('refused')
    } finally {
      setBusy(false)
    }
  }

  const frame = (children: ReactNode) => (
    <AuthScreenFrame logo={logo} headline={headline} tagline={tagline} assurance={assurance}
      legal={legal} footnote={footnote}>
      {children}
    </AuthScreenFrame>
  )

  if (phase === 'password') {
    return frame(<SetNewPassword grant={grant} purpose="invitation" logo={logo} headline={headline}
      onDone={onDone} footer={footer} />)
  }

  if (phase === 'refused' || !flow) {
    return frame(
      <div className={s.card}>
        <CardLockup logo={logo} headline={headline} />
        <h1 className={s.title}>{message('auth.mail.error.title', 'This link can’t be used')}</h1>
        <div className={s.error} role="alert" data-testid="mail-landing-refused">{refusal}</div>
        <Button type="button" variant="secondary" onClick={onDone}>
          {message('auth.action.back_to_sign_in', 'Back to sign in')}
        </Button>
        {footer}
      </div>,
    )
  }

  if (phase === 'confirmed' || phase === 'awaiting_other_address') {
    return frame(
      <div className={s.card}>
        <CardLockup logo={logo} headline={headline} />
        <h1 className={s.title}>{phase === 'confirmed'
          ? message('auth.mail.email_change.confirmed.title', 'Email address changed')
          : message('auth.mail.email_change.awaiting.title', 'One more step')}</h1>
        <p className={s.sub} role="status" data-testid="mail-landing-done">
          {phase === 'confirmed'
            ? message('auth.mail.email_change.confirmed.detail', 'Your sign-in address is changed. Use the new address the next time you sign in.')
            : message('auth.mail.email_change.awaiting.detail', 'This address is confirmed. Now open the link we sent to your other address to finish the change.')}
        </p>
        <Button type="button" variant="primary" onClick={onDone}>
          {message('auth.mail.action.continue', 'Continue')}
        </Button>
      </div>,
    )
  }

  const copy = COPY[flow]
  return frame(
    <div className={s.card}>
      <CardLockup logo={logo} headline={headline} />
      <h1 className={s.title}>{message(...copy.title)}</h1>
      <p className={s.sub}>
        {message(...copy.sub)}{' '}
        {message('auth.mail.security.scanner_note', 'Nothing happens until you continue, so a mail scanner that opened this link first has not used it.')}
      </p>
      <Button type="button" variant="primary" loading={busy} disabled={busy}
        data-testid="mail-landing-continue" onClick={() => { void proceed() }}>
        {message(...copy.action)}
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        {message('auth.action.cancel', 'Cancel')}
      </Button>
      {footer}
    </div>,
  )
}
